import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaCircleChevronRight, FaFilePdf, FaFilter } from "react-icons/fa6";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { FaChevronCircleLeft, FaChevronCircleRight, FaSpinner } from "react-icons/fa";
import { useCurrency } from '../currency/currencyContext';

Modal.setAppElement('#root');

export default function MonthlyInvoice() {
  const navigate = useNavigate();
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await axios.get("https://backend.hira-tech.co.uk/order/company-invoice-details/");
        setCompanyDetails(response.data);
      } catch (error) {
        console.error("Failed to fetch company details:", error);
      }
    };
    fetchCompanyDetails();
  }, []);

  const companyName = companyDetails?.company_name;
  const companyAddress1 = companyDetails?.address_line1;
  const companyAddress2 = companyDetails?.address_line2;
  const companyWebsite = companyDetails?.website;
  const companyEmail = companyDetails?.email;
  const companyPhone = companyDetails?.phone;

  const bankDetails = useMemo(() => ({
    bankName: companyDetails?.bank_name,
    accountTitle: companyDetails?.account_title,
    accountNumber: companyDetails?.account_number,
    sortCode: companyDetails?.sort_code,
    iban: companyDetails?.iban,
    bic: companyDetails?.bic,
  }), [companyDetails]);

  const [selectedInvoices, setSelectedInvoices] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedInvoices');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [bulkStatus, setBulkStatus] = useState("");

  const getLocalStorage = (key, defaultValue) => {
    try {
      const value = localStorage.getItem(key);
      return value !== null ? JSON.parse(value) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [selectedStatus, setSelectedStatus] = useState(() => getLocalStorage('selectedStatus', ""));
  const [startDate, setStartDate] = useState(() => getLocalStorage('startDate', ""));
  const [endDate, setEndDate] = useState(() => getLocalStorage('endDate', ""));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const savedMonth = getLocalStorage('currentMonth', null);
    const savedSelectedMonth = getLocalStorage('selectedMonth', null);
    if (savedSelectedMonth) {
      const [year, month] = savedSelectedMonth.split('-').map(Number);
      return new Date(year, month - 1, 1);
    }
    return savedMonth ? new Date(savedMonth) : new Date();
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("Case Id");
  const [sortDirection, setSortDirection] = useState("Ascending");
  const [selectedMonth, setSelectedMonth] = useState(() => getLocalStorage('selectedMonth', ""));
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = getLocalStorage('currentPage', 1);
    return Math.max(1, parseInt(savedPage, 10));
  });
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(() => getLocalStorage('selectedInvoiceId', ""));
  const [modalStartDate, setModalStartDate] = useState("");
  const [modalEndDate, setModalEndDate] = useState("");
  const [modalSelectedMonth, setModalSelectedMonth] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(() => getLocalStorage('selectedAccount', ""));

  const [entriesPerPage] = useState(10);

  const { grandTotal, totalPaymentDue } = useMemo(() => {
    let g = 0, d = 0;
    for (const o of filteredOrders) {
      g += parseFloat(o.amount) || 0;
      d += parseFloat(o.payment_due || 0) || 0;
    }
    return { grandTotal: g, totalPaymentDue: d };
  }, [filteredOrders]);

  useEffect(() => {
    localStorage.setItem('selectedStatus', JSON.stringify(selectedStatus));
    localStorage.setItem('startDate', JSON.stringify(startDate));
    localStorage.setItem('endDate', JSON.stringify(endDate));
    localStorage.setItem('currentMonth', currentMonth.toString());
    localStorage.setItem('selectedMonth', JSON.stringify(selectedMonth));
    localStorage.setItem('selectedInvoiceId', JSON.stringify(selectedInvoiceId));
    localStorage.setItem('selectedAccount', JSON.stringify(selectedAccount));
    localStorage.setItem('currentPage', JSON.stringify(currentPage));
  }, [selectedStatus, startDate, endDate, currentMonth, selectedMonth, selectedInvoiceId, selectedAccount, currentPage]);

  useEffect(() => {
    localStorage.setItem('selectedInvoices', JSON.stringify(selectedInvoices));
  }, [selectedInvoices]);

  useEffect(() => {
    setSelectedInvoices([]);
  }, [selectedStatus, startDate, endDate, selectedInvoiceId, selectedAccount, selectedMonth]);

  useEffect(() => {
    const checkboxes = document.querySelectorAll('thead input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (selectedInvoices.length > 0 && selectedInvoices.length < filteredOrders.length) {
        checkbox.indeterminate = true;
      } else {
        checkbox.indeterminate = false;
      }
    });
  }, [selectedInvoices, filteredOrders]);

  const formatDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const setMonthDates = useCallback((date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
    setModalStartDate(formatDate(firstDay));
    setModalEndDate(formatDate(lastDay));
  }, [formatDate]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await axios.get("https://backend.hira-tech.co.uk/order/api/invoices/");

        const nonCanceledOrders = response.data.filter(order =>
          order.order_details?.order_Status !== "Canceled"
        );

        setOrders(nonCanceledOrders);

        let filtered = nonCanceledOrders;

        if (searchTerm) {
          filtered = filtered.filter((order) => {
            const orderNumberMatch = order.invoice_number.includes(searchTerm);
            const customerNameMatch =
              order.customer &&
              `${order.customer.first_Name} ${order.customer.last_Name}`.toLowerCase().includes(searchTerm.toLowerCase());
            return orderNumberMatch || customerNameMatch;
          });
        }

        if (selectedStatus) {
          filtered = filtered.filter((order) => order.payment_status === selectedStatus);
        }

        if (startDate) {
          filtered = filtered.filter((order) => new Date(order.issue_date) >= new Date(startDate));
        }

        if (endDate) {
          filtered = filtered.filter((order) => new Date(order.issue_date) <= new Date(endDate));
        }

        if (selectedInvoiceId) {
          filtered = filtered.filter((order) => order.invoice_number === selectedInvoiceId);
        }

        if (selectedAccount) {
          filtered = filtered.filter((order) => {
            if (!order.customer) return false;
            const customerName = `${order.customer.first_Name} ${order.customer.last_Name}`;
            return customerName === selectedAccount;
          });
        }

        const savedSelectedMonth = getLocalStorage('selectedMonth', null);
        if (!savedSelectedMonth) {
          const current = new Date();
          const monthValue = `${current.getFullYear()}-${current.getMonth() + 1}`;
          setSelectedMonth(monthValue);
          setModalSelectedMonth(monthValue);
          setMonthDates(current);
        } else {
          const [year, month] = savedSelectedMonth.split('-').map(Number);
          const date = new Date(year, month - 1, 1);
          setMonthDates(date);
        }

        setFilteredOrders(filtered);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
      setLoading(false);
    };
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const totalPages = Math.ceil(filteredOrders.length / entriesPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [filteredOrders.length, entriesPerPage, currentPage]);

  const handleStatusChange = async (invoiceNumber, newStatus) => {
    try {
      setLoading(true);
      const response = await axios.patch(
        `https://backend.hira-tech.co.uk/order/invoice/update-status/${invoiceNumber}/`,
        { payment_status: newStatus }
      );

      setOrders(orders.map(order =>
        order.invoice_number === invoiceNumber ? response.data : order
      ));
      setFilteredOrders(filteredOrders.map(order =>
        order.invoice_number === invoiceNumber ? response.data : order
      ));
      alert("Invoice status updated successfully!");
    } catch (error) {
      console.error("Error updating invoice status:", error);
      alert("Failed to update invoice status");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedInvoices.length === 0) {
      alert("Please select at least one invoice to update");
      return;
    }
    if (!bulkStatus) {
      alert("Please select a status to apply");
      return;
    }

    try {
      setLoading(true);
      const updatePromises = selectedInvoices.map(invoiceNumber =>
        axios.patch(
          `https://backend.hira-tech.co.uk/order/invoice/update-status/${invoiceNumber}/`,
          { payment_status: bulkStatus }
        )
      );

      const responses = await Promise.all(updatePromises);

      setOrders(prevOrders => {
        const updatedOrders = [...prevOrders];
        responses.forEach(response => {
          const updatedOrder = response.data;
          const index = updatedOrders.findIndex(order => order.invoice_number === updatedOrder.invoice_number);
          if (index !== -1) updatedOrders[index] = updatedOrder;
        });
        return updatedOrders;
      });

      setFilteredOrders(prevFiltered => {
        const updatedFiltered = [...prevFiltered];
        responses.forEach(response => {
          const updatedOrder = response.data;
          const index = updatedFiltered.findIndex(order => order.invoice_number === updatedOrder.invoice_number);
          if (index !== -1) updatedFiltered[index] = updatedOrder;
        });
        return updatedFiltered;
      });

      setSelectedInvoices([]);
      setBulkStatus("");
      alert("Selected invoices updated successfully!");
    } catch (error) {
      console.error("Error updating invoices:", error);
      alert("Failed to update some or all invoices");
    } finally {
      setLoading(false);
    }
  };

  const toggleInvoiceSelection = useCallback((invoiceNumber) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceNumber)
        ? prev.filter(id => id !== invoiceNumber)
        : [...prev, invoiceNumber]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedInvoices(prev => {
      if (prev.length === filteredOrders.length && filteredOrders.length > 0) {
        return [];
      }
      return filteredOrders.map(order => order.invoice_number);
    });
  }, [filteredOrders]);

  const clearAllSelection = useCallback(() => setSelectedInvoices([]), []);

  const paginate = (pageNumber) => {
    const totalPages = Math.ceil(filteredOrders.length / entriesPerPage);
    const validPage = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(validPage);
  };

  useEffect(() => {
    let filtered = orders.filter(order =>
      order.order_details?.order_Status !== "Canceled"
    );

    if (searchTerm) {
      filtered = filtered.filter((order) => {
        const orderNumberMatch = order.invoice_number.includes(searchTerm);
        const customerNameMatch =
          order.customer &&
          `${order.customer.first_Name} ${order.customer.last_Name}`.toLowerCase().includes(searchTerm.toLowerCase());
        return orderNumberMatch || customerNameMatch;
      });
    }
    if (selectedStatus) filtered = filtered.filter((order) => order.payment_status === selectedStatus);
    if (startDate) filtered = filtered.filter((order) => new Date(order.issue_date) >= new Date(startDate));
    if (endDate) filtered = filtered.filter((order) => new Date(order.issue_date) <= new Date(endDate));
    if (selectedInvoiceId) filtered = filtered.filter((order) => order.invoice_number === selectedInvoiceId);
    if (selectedAccount) {
      filtered = filtered.filter((order) => {
        if (!order.customer) return false;
        const customerName = `${order.customer.first_Name} ${order.customer.last_Name}`;
        return customerName === selectedAccount;
      });
    }
    setFilteredOrders(filtered);
  }, [searchTerm, selectedStatus, startDate, endDate, orders, selectedInvoiceId, selectedAccount]);

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    if (direction === "prev") newMonth.setMonth(newMonth.getMonth() - 1);
    else newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);

    const monthValue = `${newMonth.getFullYear()}-${newMonth.getMonth() + 1}`;
    setSelectedMonth(monthValue);
    setModalSelectedMonth(monthValue);
    setMonthDates(newMonth);
  };

  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  const monthOptions = useMemo(() => {
    const months = [];
    const current = new Date();
    const startYear = 2023;
    const endYear = current.getFullYear();
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month, 1);
        months.push({
          value: `${year}-${month + 1}`,
          label: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        });
      }
    }
    return months.reverse();
  }, []);

  const generateMonthOptions = useCallback(() => monthOptions, [monthOptions]);

  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split('-').map(Number);
    const newMonth = new Date(year, month - 1, 1);
    setModalSelectedMonth(e.target.value);
    setMonthDatesForModal(newMonth);
  };

  const setMonthDatesForModal = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setModalStartDate(formatDate(firstDay));
    setModalEndDate(formatDate(lastDay));
  };

  const invoicesForSelectedMonth = useMemo(() => {
    if (!modalSelectedMonth) return orders;
    const [year, month] = modalSelectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return orders.filter(order => {
      const orderDate = new Date(order.issue_date);
      return orderDate >= firstDay && orderDate <= lastDay;
    });
  }, [modalSelectedMonth, orders]);

  const uniqueAccounts = useMemo(() => {
    const accounts = new Set();
    for (const order of orders) {
      if (order.customer) {
        accounts.add(`${order.customer.first_Name} ${order.customer.last_Name}`);
      }
    }
    return Array.from(accounts).sort();
  }, [orders]);

  const openFilterModal = () => {
    setModalSelectedMonth(selectedMonth);
    setModalStartDate(startDate);
    setModalEndDate(endDate);
    setIsFilterModalOpen(true);
  };

  const applyFilters = () => {
    setSelectedMonth(modalSelectedMonth);
    setStartDate(modalStartDate);
    setEndDate(modalEndDate);
    setSelectedInvoiceId(selectedInvoiceId);
    setSelectedAccount(selectedAccount);

    const [year, month] = modalSelectedMonth.split('-').map(Number);
    setCurrentMonth(new Date(year, month - 1, 1));

    setIsFilterModalOpen(false);
  };

  const resetFilters = () => {
    const current = new Date();
    const monthValue = `${current.getFullYear()}-${current.getMonth() + 1}`;

    localStorage.removeItem('selectedStatus');
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('currentMonth');
    localStorage.removeItem('selectedMonth');
    localStorage.removeItem('selectedInvoiceId');
    localStorage.removeItem('selectedAccount');
    localStorage.removeItem('selectedInvoices');

    setSelectedStatus("");
    setSelectedInvoiceId("");
    setSelectedAccount("");
    setSelectedMonth(monthValue);
    setModalSelectedMonth(monthValue);
    setMonthDates(current);
    setCurrentMonth(current);
    setSelectedInvoices([]);
  };

  // ✅ Extract address from ANY possible location in the data
  const extractAddress = (data) => {
    if (!data) return null;

    const candidates = [
      data.address,
      data.delivery_Address,
      data.delivery_address,
      data.shipping_Address,
      data.shipping_address,
      data.customer?.address,
      data.order_details?.address,
      data.order_details?.delivery_Address,
      data.order_details?.shipping_Address,
    ];

    for (const addr of candidates) {
      if (addr && typeof addr === 'object') {
        const parts = [];
        if (addr.house_Number) parts.push(addr.house_Number);
        if (addr.street_Number) parts.push(addr.street_Number);
        if (addr.area) parts.push(addr.area);
        if (addr.city) parts.push(addr.city);
        if (addr.postal_Code) parts.push(addr.postal_Code);
        if (parts.length > 0) return parts.join(', ');
      }
    }

    const stringCandidates = [
      data.address,
      data.delivery_Address,
      data.shipping_Address,
      data.customer?.address,
      data.order_details?.address,
    ];
    for (const s of stringCandidates) {
      if (typeof s === 'string' && s.trim()) return s.trim();
    }

    return null;
  };

  // ✅ Fetch address for a single order (tries list data first, then multiple IDs on detail endpoint)
  const fetchAddressForOrder = async (order) => {
    // First try the list data
    const fromList = extractAddress(order);
    if (fromList) return fromList;

    // Then try the invoice detail endpoint with both IDs
    const idsToTry = [order?.order_details?.order_Id, order?.invoice_number].filter(Boolean);
    for (const id of idsToTry) {
      try {
        const res = await axios.get(`https://backend.hira-tech.co.uk/order/invoice/${id}/`);
        const addr = extractAddress(res.data);
        if (addr) return addr;
      } catch (e) {
        // Silent fail — try next id
      }
    }
    return "No delivery address available";
  };

  // ✅ Build the PDF layout for one account's group of orders
  const renderAccountSection = (doc, accountName, accountOrders, address, options) => {
    const { isFirstAccount, startDate, endDate, companyInfo, bank, formatPriceFn, convertPriceFn } = options;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const marginTop = 30;

    // Only add a new page when starting a new account (not the first one)
    if (!isFirstAccount) {
      doc.addPage();
    }

    // --- Logo ---
    try {
      doc.addImage("/logo.ico", "PNG", 7, 10, 75, 35);
    } catch (e) {
      // Logo optional
    }

    // --- Company info ---
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(companyInfo.name || '', margin, 20 + marginTop);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(companyInfo.address1 || '', margin, 27 + marginTop);
    doc.text(companyInfo.address2 || '', margin, 32 + marginTop);

    // --- Ship-To-Party ---
    doc.setFont("helvetica", "bold");
    doc.text("Ship-To-Party", margin, 47 + marginTop);
    doc.setFont("helvetica", "normal");

    const firstOrder = accountOrders[0];
    const customerName = accountName;

    const margiinTop = 50;
    doc.text(customerName, margin, 54 + marginTop);

    const addressLines = address && address !== "No delivery address available"
      ? (address.match(/.{1,42}(\s|$)/g)?.map(s => s.trim()).filter(Boolean) || [address])
      : ["No delivery address available"];

    let currentAddressY = 59 + marginTop;
    addressLines.forEach((line) => {
      doc.text(line, margin, currentAddressY);
      currentAddressY += 4;
    });

    const statementY = currentAddressY + 4;
    doc.setFont("helvetica", "bold");
    doc.text(`Account statement from ${startDate || "all"} to ${endDate || "all"}`, margin, statementY);
    doc.setFont("helvetica", "normal");

    // --- Statement info box (top-right) ---
    const boxWidth = 87;
    const boxX = pageWidth - margin - boxWidth;
    const boxHeight = 55;
    const invoiceMarginTop = 25;

    doc.setDrawColor(0, 74, 173);
    doc.rect(boxX, 20 + invoiceMarginTop, boxWidth, boxHeight);
    doc.setFillColor(0, 74, 173);
    doc.rect(boxX, 20 + invoiceMarginTop, boxWidth, 10, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Statement", boxX + 5, 27 + invoiceMarginTop);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let invoiceY = 40 + invoiceMarginTop;

    const infoLines = [
      "Page: 1 of 1",
      `Date: ${new Date().toLocaleDateString()}`,
      `Customer Name: ${customerName}`,
      `Email:  ${firstOrder.customer?.email || "N/A"}`,
      `Contact Number: ${firstOrder.customer?.phone_Number || "N/A"}`,
    ];

    infoLines.forEach((line) => {
      doc.text(line, boxX + 5, invoiceY);
      invoiceY += 5;
    });

    // --- Invoice table ---
    const tableStartY = Math.max(statementY + 15, 125);
    let currentY = tableStartY;

    const tableData = accountOrders.map((order) => [
      order.issue_date,
      order.invoice_number,
      order.order_details?.order_Tracking_Id || "N/A",
      formatPriceFn(order.amount),
      formatPriceFn(order.payment_due),
      order.payment_status,
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [["Date", "Invoice Number", "Order Tracking", "Amount", "Payment Due", "Status"]],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2, minCellHeight: 8, valign: "middle" },
      headStyles: { fillColor: "#004aad", textColor: 255, fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 32 }, 1: { cellWidth: 32 }, 2: { cellWidth: 32 },
        3: { cellWidth: 32 }, 4: { cellWidth: 32 }, 5: { cellWidth: 30 },
      },
      margin: { left: margin, right: margin },
      theme: "grid",
      didDrawPage: function (data) { currentY = data.cursor.y; },
    });

    // --- Per-account totals ---
    const accountTotalAmount = accountOrders.reduce((sum, o) => sum + convertPriceFn(o.amount), 0);
    const accountPaymentDue = accountOrders.reduce((sum, o) => sum + convertPriceFn(o.payment_due || 0), 0);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 2,
      body: [[endDate || new Date().toLocaleDateString("en-GB"), "", "", formatPriceFn(accountPaymentDue), ""]],
      styles: { fillColor: "#004aad", textColor: 255, fontSize: 9, cellPadding: 2, minCellHeight: 8, valign: "middle" },
      columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 40 }, 2: { cellWidth: 40 }, 3: { cellWidth: 40 }, 4: { cellWidth: 30 } },
      margin: { left: margin, right: margin },
      theme: "grid",
    });

    currentY = doc.lastAutoTable.finalY + 10;

    const summaryLeft = pageWidth - margin - 75;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 74, 173);
    doc.text(`Grand Total (Account): ${formatPriceFn(accountTotalAmount)}`, summaryLeft, currentY);
    currentY += 6;
    doc.text(`Payment Due (Account): ${formatPriceFn(accountPaymentDue)}`, summaryLeft, currentY);
    currentY += 10;
    doc.setTextColor(0, 0, 0);

    // --- Footer for this account ---
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    const leftFooter = [
      "Terms of payment: 30 days end-of-month",
      `Terms of Delivery: ${firstOrder.order_details?.deliver_Method || "N/A"}`,
    ];
    const rightFooter = [
      `Phone: ${companyInfo.phone || ''}`,
      `Website: ${companyInfo.website || ''}`,
      `Email: ${companyInfo.email || ''}`,
    ];

    const footerStartY = currentY;
    const spacing = 5;
    const leftX = margin;
    const rightX = pageWidth / 2 + 5;

    for (let i = 0; i < Math.max(leftFooter.length, rightFooter.length); i++) {
      if (leftFooter[i]) doc.text(leftFooter[i], leftX, footerStartY + i * spacing);
      if (rightFooter[i]) doc.text(rightFooter[i], rightX, footerStartY + i * spacing);
    }

    const bankDetailsY = footerStartY + Math.max(leftFooter.length, rightFooter.length) * spacing + 5;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details:", leftX, bankDetailsY);
    doc.setFont("helvetica", "normal");

    doc.text("Bank Name: " + (bank?.bankName || ''), leftX, bankDetailsY + 4);
    doc.text("Account Title: " + (bank?.accountTitle || ''), leftX, bankDetailsY + 8);
    doc.text("Sort Code: " + (bank?.sortCode || ''), leftX, bankDetailsY + 12);
    doc.text("Account Number: " + (bank?.accountNumber || ''), leftX, bankDetailsY + 16);
    doc.text("BIC: " + (bank?.bic || ''), leftX, bankDetailsY + 24);
    doc.text("IBAN: " + (bank?.iban || ''), leftX, bankDetailsY + 20);
  };

  const handleDownload = async () => {
    // ✅ Decide source set: selected invoices if any are checked, otherwise the filtered list
    const useSelection = selectedInvoices.length > 0;
    const sourceOrders = useSelection
      ? filteredOrders.filter(o => selectedInvoices.includes(o.invoice_number))
      : filteredOrders;

    if (sourceOrders.length === 0) {
      alert("No orders to download. Please adjust your filters.");
      return;
    }

    setDownloading(true);

    try {
      // ✅ Group orders by account name (alphabetical)
      const grouped = new Map();
      for (const order of sourceOrders) {
        const name = order.customer
          ? `${order.customer.first_Name} ${order.customer.last_Name}`
          : "Unknown Customer";
        if (!grouped.has(name)) grouped.set(name, []);
        grouped.get(name).push(order);
      }

      // Sort account names alphabetically
      const sortedAccounts = Array.from(grouped.keys()).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      // ✅ Fetch address for each account (from its first order)
      const accountAddresses = {};
      for (const acct of sortedAccounts) {
        const firstOrder = grouped.get(acct)[0];
        accountAddresses[acct] = await fetchAddressForOrder(firstOrder);
      }

      console.log("📊 Grouped accounts:", sortedAccounts);
      console.log("📍 Addresses:", accountAddresses);

      setTimeout(() => {
        const doc = new jsPDF("p", "mm", "a4");

        const companyInfo = {
          name: companyName,
          address1: companyAddress1,
          address2: companyAddress2,
          phone: companyPhone,
          website: companyWebsite,
          email: companyEmail,
        };

        sortedAccounts.forEach((acct, idx) => {
          renderAccountSection(doc, acct, grouped.get(acct), accountAddresses[acct], {
            isFirstAccount: idx === 0,
            startDate,
            endDate,
            companyInfo,
            bank: bankDetails,
            formatPriceFn: formatPrice,
            convertPriceFn: convertPrice,
          });
        });

        const suffix = useSelection ? `selected_${sourceOrders.length}` : `${startDate || "all"}_to_${endDate || "all"}`;
        doc.save(`invoices_${suffix}.pdf`);
        setDownloading(false);
      }, 100);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
      setDownloading(false);
    }
  };

  const { currentEntries, totalPages } = useMemo(() => {
    const total = Math.ceil(filteredOrders.length / entriesPerPage);
    const last = currentPage * entriesPerPage;
    const first = last - entriesPerPage;
    return { currentEntries: filteredOrders.slice(first, last), totalPages: total };
  }, [filteredOrders, currentPage, entriesPerPage]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;

  const selectedInvoicesSet = useMemo(() => new Set(selectedInvoices), [selectedInvoices]);

  const customStyles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
    },
    content: {
      position: 'fixed', top: '0', right: '0', left: 'auto', bottom: '0',
      width: '550px', maxWidth: '90vw', border: 'none', background: '#fff',
      overflow: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: '0',
      padding: '0', boxShadow: '-2px 0 10px rgba(0,0,0,0.3)',
      transform: 'translateX(0)', transition: 'transform 0.3s ease-out',
    }
  };

  return (
    <>
      <div className="order-container">
        <div className="header-section">
          <div className="month-header">
            <h2>Statements</h2>
          </div>
          <div className="summary-section">
            <div className="month-navigation">
              <button onClick={() => navigateMonth("prev")}>
                <FaChevronCircleLeft style={{ marginRight: '5px', position: 'relative', top: '2px' }} />Previous Month
              </button>
              <span className="month-display">{monthName}
                <br />
                (Grand Total: {formatPrice(grandTotal)}) <br />
                (Payment Due: {formatPrice(totalPaymentDue)})
              </span>
              <button onClick={() => navigateMonth("next")}>
                Next Month<FaChevronCircleRight style={{ marginLeft: '5px', position: 'relative', top: '2px' }} />
              </button>
            </div>
            <div></div>
          </div>
          <button className="filter-button" onClick={openFilterModal}>
            <FaFilter /> Filters
          </button>
        </div>

        <div className="bulk-update-section">
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="bulk-status-select"
            disabled={selectedInvoices.length === 0 || loading}
          >
            <option value="">Select Status</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
          </select>
          <button
            onClick={handleBulkStatusUpdate}
            className="bulk-update-button"
            disabled={selectedInvoices.length === 0 || !bulkStatus || loading}
          >
            Update Selected ({selectedInvoices.length})
          </button>
          {selectedInvoices.length > 0 && (
            <button onClick={clearAllSelection} className="clear-selection-button">Clear Selection</button>
          )}
        </div>

        <button
          onClick={handleDownload}
          className="download-button"
          disabled={filteredOrders.length === 0 || downloading}
        >
          <FaFilePdf /> {downloading ? "Generating PDF..." : (selectedInvoices.length > 0 ? `Download PDF (${selectedInvoices.length} selected)` : "Download PDF")}
        </button>

        {loading ? (
          <div className="loading-message"><FaSpinner className="spinner-icon" /></div>
        ) : (
          <div className="table-wrapper">
            <div className="page-info">
              Showing {indexOfFirstEntry + 1}-{Math.min(indexOfLastEntry, filteredOrders.length)} of {filteredOrders.length} entries
            </div>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={filteredOrders.length > 0 && selectedInvoices.length === filteredOrders.length}
                      onChange={toggleSelectAll}
                      ref={(checkbox) => {
                        if (checkbox) {
                          checkbox.indeterminate = selectedInvoices.length > 0 && selectedInvoices.length < filteredOrders.length;
                        }
                      }}
                    />
                  </th>
                  <th>Date</th>
                  <th>Invoice Number</th>
                  <th>Order Number</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Status Update</th>
                  <th>Total</th>
                  <th>Payment Due</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {currentEntries.map((order, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedInvoicesSet.has(order.invoice_number)}
                        onChange={() => toggleInvoiceSelection(order.invoice_number)}
                      />
                    </td>
                    <td>{order.issue_date}</td>
                    <td>{order.invoice_number}</td>
                    <td>{order.order_details?.order_Tracking_Id || "N/A"}</td>
                    <td>
                      {order.customer
                        ? `${order.customer.first_Name} ${order.customer.last_Name}`
                        : "Unknown"}
                    </td>
                    <td>{order.payment_status}</td>
                    <td>
                      <select
                        className={`status-select ${order.payment_status === 'Paid' ? 'status-paid' : 'status-pending'}`}
                        value={order.payment_status}
                        onChange={(e) => handleStatusChange(order.invoice_number, e.target.value)}
                        disabled={loading}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </td>
                    <td>{formatPrice(order.amount)}</td>
                    <td>{formatPrice(order.payment_due)}</td>
                    <td className="arrow-cell">
                      <button
                        className="navigate-button"
                        onClick={() => navigate(`/admin/invoice-detail/${order.order_details?.order_Id}`)}
                      >
                        <FaCircleChevronRight className="arrow-icon" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredOrders.length > entriesPerPage && (
              <div className="pagination-controls">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={currentPage === number ? 'active' : ''}
                  >
                    {number}
                  </button>
                ))}
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="selection-info">
        {selectedInvoices.length > 0 && (
          <span>
            ✅ {selectedInvoices.length} of {filteredOrders.length} invoices selected
            {selectedInvoices.length === filteredOrders.length && " (All Selected)"}
          </span>
        )}
      </div>

      <Modal
        isOpen={isFilterModalOpen}
        onRequestClose={() => setIsFilterModalOpen(false)}
        contentLabel="Filter Modal"
        style={customStyles}
        ariaHideApp={false}
      >
        <div className="modal-contentt">
          <div className="model-header">
            <h2>Change Filters</h2>
            <button className="close-button" onClick={() => setIsFilterModalOpen(false)}>×</button>
          </div>
          <div className="current-filters">
            <p><strong>Current Filters:</strong></p>
            <p>Month: {modalSelectedMonth
              ? new Date(modalSelectedMonth.split('-')[0], modalSelectedMonth.split('-')[1] - 1)
                  .toLocaleString('default', { month: 'long', year: 'numeric' })
              : 'All'}</p>
            <p>Date Range: {modalStartDate} to {modalEndDate}</p>
            <p>Account: {selectedAccount || 'All'}</p>
          </div>
          <div className="filter-section">
            <div className="filter-group">
              <label>By Month</label>
              <select value={modalSelectedMonth} onChange={handleMonthChange}>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Invoice ID</label>
              <select value={selectedInvoiceId} onChange={(e) => setSelectedInvoiceId(e.target.value)}>
                <option value="">All Invoices</option>
                {invoicesForSelectedMonth.map((order) => (
                  <option key={order.invoice_number} value={order.invoice_number}>{order.invoice_number}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Account</label>
              <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                <option value="">All Accounts</option>
                {uniqueAccounts.map((account, index) => (
                  <option key={index} value={account}>{account}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-group">
              <label>Start Date</label>
              <input type="date" value={modalStartDate} onChange={(e) => setModalStartDate(e.target.value)} placeholder="dd/mm/yyyy" />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input type="date" value={modalEndDate} onChange={(e) => setModalEndDate(e.target.value)} placeholder="dd/mm/yyyy" />
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-group">
              <label>Status</label>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option>Invoice Id</option>
                <option>Date</option>
                <option>Amount</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Sort Direction</label>
              <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)}>
                <option>Ascending</option>
                <option>Descending</option>
              </select>
            </div>
          </div>
          <div className="modal-buttons">
            <button onClick={resetFilters}>Reset</button>
            <button onClick={() => setIsFilterModalOpen(false)}>Cancel</button>
            <button onClick={applyFilters} className="apply-button">Apply</button>
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .order-container {
          padding: 20px;
          transition: all 0.3s ease;
        }
        .header-section {
          display: flex;
          flex-direction: column;
          margin-bottom: 20px;
        }
        .month-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .month-navigation {
          display: flex;
          align-items: center;
          gap: 15px;
        }  
        .month-navigation button {
          background: #004aad;
          color: white;
          border: none;
          padding: 8px 15px;
          border-radius: 4px;
          cursor: pointer;
        }    
        .month-display {
          font-weight: bold;
          font-size: 1.1rem;
        }
        .summary-section {
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 15px;
        }
        .filter-button {
          align-self: flex-end;
          background: #004aad;
          color: white;
          border: none;
          padding: 8px 15px;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          margin-bottom: 15px;
        }
        .bulk-update-section {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          align-items: center;
          flex-wrap: wrap;
        }
        .bulk-status-select {
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #ccc;
        }
        .bulk-update-button {
          background: #004aad;
          color: white;
          border: none;
          padding: 8px 15px;
          border-radius: 4px;
          cursor: pointer;
        }
        .bulk-update-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .clear-selection-button {
          background: #ff4757;
          color: white;
          border: none;
          padding: 8px 15px;
          border-radius: 4px;
          cursor: pointer;
        }
        .clear-selection-button:hover {
          background: #ff3742;
        }
        .search-row {
          margin-bottom: 15px;
        }
        .search-bar {
          width: 100%;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #ccc;
          font-size: 14px;
        }
        .download-button {
          background-color: #004aad;
          color: #fff;
          font-weight: bold;
          padding: 10px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .download-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .table-wrapper {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }
        th, td {
          padding: 10px;
          border: 1px solid #ddd;
          text-align: left;
        }  
        th {
          background: #004aad;
          color: white;
        }
        .arrow-cell {
          text-align: center;
        }
        .navigate-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .pagination-controls {
          margin-top: 20px;
          display: flex;
          justify-content: center;
          gap: 5px;
        }
        .pagination-controls button {
          padding: 5px 10px;
          border: 1px solid #ddd;
          background: white;
          cursor: pointer;
          border-radius: 4px;
        }
        .pagination-controls button:hover:not(:disabled) {
          background: #004aad;
          color: white;
        }
        .pagination-controls button.active {
          background: #004aad;
          color: white;
          border-color: #004aad;
        }
        .pagination-controls button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .arrow-icon {
          color: #004aad;
          transition: transform 0.3s ease-in-out;
        }
        .arrow-icon:hover {
          transform: translateX(5px);
        }
        .loading-message {
          text-align: center;
          margin-top: 20px;
        }
        .selection-info {
          padding: 10px;
          background: #e8f5e8;
          border: 1px solid #2ecc71;
          border-radius: 4px;
          margin: 10px 0;
          text-align: center;
          font-weight: bold;
          color: #27ae60;
        }
        input[type="checkbox"] {
          cursor: pointer;
          transform: scale(1.2);
        }
        input[type="checkbox"]:checked {
          accent-color: #004aad;
        }
        
        .modal-contentt {
          display: flex;
          flex-direction: column;
          height: 100vh;
          padding: 25px;
          overflow-y: auto;
        }

        .model-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #eee;
        }

        .model-header h2 {
          margin: 0;
          color: #004aad;
          font-size: 1.5rem;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 35px;
          height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .close-button:hover {
          background: #f5f5f5;
          color: #333;
        }

        .current-filters {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          border-left: 4px solid #004aad;
        }

        .current-filters p {
          margin: 5px 0;
          font-size: 14px;
          color: #555;
        }

        .filter-section {
          border-bottom: 1px solid #eee;
          padding-bottom: 20px;
          margin-bottom: 20px;
        }

        .filter-group {
          margin-bottom: 15px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-weight: bold;
          color: #333;
          font-size: 14px;
        }

        .filter-group select,
        .filter-group input {
          padding: 10px;
          border-radius: 6px;
          border: 1px solid #ddd;
          font-size: 14px;
          background: white;
          width: 100%;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #004aad;
          box-shadow: 0 0 0 2px rgba(0, 74, 173, 0.1);
        }

        .modal-buttons {
          display: flex;
          gap: 10px;
          margin-top: auto;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .modal-buttons button {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .modal-buttons button:first-child {
          background: #6c757d;
          color: white;
        }

        .modal-buttons button:first-child:hover {
          background: #5a6268;
        }

        .modal-buttons button:nth-child(2) {
          background: #f8f9fa;
          color: #333;
          border: 1px solid #ddd;
        }

        .modal-buttons button:nth-child(2):hover {
          background: #e2e6ea;
        }

        .apply-button {
          background: #004aad;
          color: white;
        }

        .apply-button:hover {
          background: #003d8f;
        }

        .loading-message {
          text-align: center;
          margin-top: 20px;
        }
        
        .spinner-icon {
          animation: spin 1s linear infinite;
          color: #004aad;
          font-size: 24px;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .page-info {
          margin: 10px 0;
          text-align: center;
          color: #666;
        }

        @media (max-width: 768px) {
          .month-header {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }
          .month-navigation {
            width: 100%;
            justify-content: space-between;
          }
        }

        @media (max-width: 600px) {
          .modal-contentt {
            padding: 15px;
          }
          
          .filter-group select,
          .filter-group input {
            font-size: 16px;
          }
        }

        @media (max-width: 480px) {
          .bulk-update-section {
            flex-direction: column;
            align-items: stretch;
          }
          .bulk-update-section > * {
            width: 100%;
          }
          .modal-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
}